import { Box, Flex, Icon, Text, styled } from '@kandji-inc/nectar-ui';
import { i18n } from 'i18n';
import { marked } from 'marked';
import * as React from 'react';
import type { MessageComponent } from '../ai.types';
import { ChatFeedback } from './chat-feedback';
import KaiBeeIcon from './kai-bee-chat-icon.svg';
import { LoadingIndicator } from './loading-indicator';

export const Message = ({
  sender,
  initials,
  components,
  onClickUrl,
  isLastBotMessage,
  chatSessionId,
  messageId,
  align = 'left',
}: {
  sender: string;
  components: MessageComponent[];
  initials: string;
  onClickUrl: (url: string) => void;
  isLastBotMessage: boolean;
  chatSessionId: string;
  messageId: string;
  align?: 'left' | 'right';
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = React.useState(false);
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // istanbul ignore next
  const handleMouseEnter = () => {
    setIsHovered(true);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  // istanbul ignore next
  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isFeedbackOpen) {
        setIsHovered(false);
      }
    }, 300);
  };

  const hasLoadingComponent = components.some(
    (component) => component?.component_type === 'loading',
  );

  return (
    <Flex
      gap="lg"
      alignItems="start"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      css={{
        alignSelf: align === 'right' ? 'flex-end' : 'flex-start',
        flexDirection: align === 'right' ? 'row-reverse' : 'row',
        width: '100%',
      }}
    >
      <Avatar isBot={sender === 'bot'} initials={initials} />
      <Flex
        flow="column"
        gap="xs"
        alignItems={align === 'right' ? 'end' : 'start'}
        css={{ width: '100%' }}
      >
        <Text
          css={{
            alignSelf: align === 'right' ? 'flex-end' : 'flex-start',
            color: '$neutral70',
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '16px /* 133.333% */',
            textAlign: align === 'right' ? 'right' : 'left',
          }}
        >
          {sender === 'user' ? i18n.t('You') : i18n.t('Kai')}
        </Text>
        {components?.map((component, index) => {
          switch (component?.component_type) {
            case 'text': {
              if (
                !component.data.text.includes('Failed') &&
                !component.data.text.includes('I am unable to do aggregations')
              ) {
                const parsed = marked(component.data.text);
                return (
                  <Flex
                    flow="column"
                    gap="sm"
                    key={`${component.component_type}-${index}`}
                  >
                    <StyledMessageContainer
                      // biome-ignore lint/security/noDangerouslySetInnerHtml: safe
                      dangerouslySetInnerHTML={{ __html: parsed }}
                      css={{
                        alignSelf:
                          align === 'right' ? 'flex-end' : 'flex-start',
                        textAlign: align === 'right' ? 'right' : 'left',
                      }}
                    />
                  </Flex>
                );
              }
              return null;
            }
            case 'url': {
              return (
                <Box
                  data-testid="kai-view-results-button"
                  key={`${component.component_type}-${index}`}
                  onClick={() => onClickUrl(component.data.url)}
                  css={{
                    cursor: 'pointer',
                    color: '$blue50',
                    ':hover': {
                      textDecoration: 'underline',
                      color: '$blue70',
                    },
                  }}
                >
                  <Text css={{ fontWeight: '$medium' }}>
                    {i18n.t('View results in Prism')}
                  </Text>
                </Box>
              );
            }
            case 'loading': {
              return (
                <LoadingIndicator
                  key={`${component.component_type}-${index}`}
                />
              );
            }
            default: {
              return null;
            }
          }
        })}
        {sender === 'bot' &&
          !hasLoadingComponent &&
          (isLastBotMessage || isHovered || isFeedbackOpen) && (
            <ChatFeedback
              onOpenChange={(isOpen) => setIsFeedbackOpen(isOpen)}
              chatSessionId={chatSessionId}
              messageId={messageId}
              isLastBotMessage={isLastBotMessage}
            />
          )}
      </Flex>
    </Flex>
  );
};

const StyledMessageContainer = styled(Box, {
  '& h3': {
    alignSelf: 'stretch',
    color: '$neutral90',
    fontFeatureSettings: "'liga' off",
    fontSize: '$2',
    fontStyle: 'normal',
    fontWeight: '$medium',
    lineHeight: '20px /* 142.857% */',
  },
  '& p': {
    alignSelf: 'stretch',
    color: '$neutral90',
    fontFeatureSettings: "'liga' off",
    fontSize: '$2',
    fontStyle: 'normal',
    fontWeight: '$regular',
    lineHeight: '20px /* 142.857% */',
  },
});

const Avatar = ({ isBot, initials }: { isBot: boolean; initials: string }) => (
  <Flex alignItems="start" gap="sm">
    {!isBot ? (
      <Flex
        gap="xs"
        justifyContent="center"
        alignItems="center"
        css={{
          width: '28px',
          height: '28px',
          padding: '$2',
          borderRadius: '4px',
          background: '#FFBC00',
          color: '$neutral90',
        }}
      >
        <Text
          css={{
            fontWeight: '$medium',
            fontSize: '$1',
          }}
        >
          {initials}
        </Text>
      </Flex>
    ) : (
      <Flex
        gap="xs"
        justifyContent="center"
        alignItems="center"
        css={{
          width: '28px',
          height: '28px',
          padding: '2px',
          borderRadius: '4px',
          background: '$neutral0',
        }}
      >
        <img src={KaiBeeIcon} alt="Kai Bee Logo" width={28} height={28} />
      </Flex>
    )}
  </Flex>
);
